import React, { Ref, forwardRef, useEffect, useRef } from "react";
import { useState, useContext } from "react";
import { Button, Card, Table, Modal, Alert, Form, InputGroup, CloseButton, NavItem } from "react-bootstrap";
import { Loading, LoadingIcon } from "../Loading";
import { lddDriveObj, FileDrive_Funcs, hasSpecialCharacter } from "./filedriveFuncs"
import { FolderEntry, FileSystem, FileEntry, ArchiveData, TreeNode } from "./filebase";
import { StateTypeContext } from "../clientportal/types";
import { ActivityCenterContext } from "../../Context/ActivityCenter";
import DocumentRow from "./DocumentRow";
import FolderRow from "./FolderRow";
import { setCookie, getCookie } from "../topbars/topBarFuncs";
import {ListPlaceholder} from "../ui/listPlaceholder/listPlaceholder"

export const folderSpecialChars: string[] = ["|", ":", "?", "*", "%", "#", "\\", "/", "<", ">", ".", "{", "}", "\"", "`"];
const fileSpecialChars: string[] = ["|", ":", "?", "*", "%", "#", "\\", "/", "<", ">", ".", "{", "}", "\"", "`"];

type FileDriveProps = {
  closeMyDrive: () => void;
  SessionID: string;
  AppCode: string;
  UserID: string;
  matterNumber: string;
  AppID: string;
  portalState: StateTypeContext;
  portalService?: any; // can't specify PortalService type because portal.service.ts has dependencies in redux and don't apply to will export
  FileDriveIndicators?: {
    MyDriveHasNewDoc: boolean,
    Activity: boolean,
    IsPageDirty: boolean,
  },
  // These props make the component "dumb" and reusable (optional for backward compatibility)
  preInitializedDrive?: FileSystem | null;
  preInitializedFolder?: FolderEntry | null;
  // When provided, the drive renders as a file picker: clicking a file selects it and the
  // create-folder/upload affordances are hidden (used by the email attachment picker).
  onPickFile?: (doc: FileEntry) => void;
};

export enum SortDir {
    Up = "up",
    Down = 'down',
    None = 'none',
}

/** Split display name into base + extension (uses last dot). */
function splitFileName(fileName: string): { base: string; ext: string } {
    const i = fileName.lastIndexOf(".");
    if (i <= 0) return { base: fileName, ext: "" };
    return { base: fileName.substring(0, i), ext: fileName.substring(i + 1) };
}

type UploadQueueItem = { file: File; fullName: string };

export const FileDrive = forwardRef(({closeMyDrive, SessionID, AppCode, UserID, matterNumber, AppID, portalState, portalService, FileDriveIndicators, preInitializedDrive, preInitializedFolder, onPickFile}: FileDriveProps, ref: Ref<HTMLDivElement>) => {
    const activityCenter = useContext(ActivityCenterContext);
    const [isDriveLoading, setIsDriveLoading] = useState<boolean>(true); // Start in loading state
    const [FolderContent, setFolderContent] = useState<Array<FileEntry | FolderEntry>>([]);
    const [Drive, setDrive] = useState<FileSystem>(undefined);
    const [loadingDrive, setLoadingDrive] = useState<boolean>(false);

    // Backward compatibility: If no pre-initialized props, use original logic
    let config: lddDriveObj = {
        type: 1,
        sharepointConfig: {
            rootDrive: "ldd_fake_drive_root",
            host: "ldd_fake_drive_tenant",
            urlOverwrite: `/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${AppCode}/${UserID}/${AppID}/filedrive/sendToSharepoint?SessionID=${SessionID}`,
            AppID: AppID,
            AppCode: AppCode,
            UserID: UserID,
        }
    }

    const ActivateDrive = async (): Promise<void> => {
        setIsDriveLoading(true); // Start loading
        let drive: FileSystem = FileDrive_Funcs.createLddDrive(config, matterNumber, setIsDriveLoading, setFolderContent, activityCenter);
        await FileDrive_Funcs.getMainFolder();
        setDrive(drive);
        setLoadingDrive(false);
        setIsDriveLoading(false); // End loading
    }

    // This useEffect handles BOTH initialization paths safely
    useEffect(() => {
        // TODO: This component uses a legacy self-initialization path.
        // Refactor all consuming apps to provide the preInitializedFolder prop,
        // then remove the 'else' block and the ActivateDrive function
        
        if (preInitializedFolder) {
            // --- PATH A: The New, Correct Way ---
            // The parent component did the work. Just use the prop to set content
            const updateContents = async () => {
                setIsDriveLoading(true);
                try {
                    const contents = await preInitializedFolder.getContentList();
                    setFolderContent(contents);
                    // Update the FileDrive_Funcs singleton so other functions work
                    FileDrive_Funcs.setExistingDrive(preInitializedDrive, preInitializedFolder, setIsDriveLoading, setFolderContent, activityCenter);
                } catch (error) {
                    console.error("Failed to get folder contents:", error);
                    setFolderContent([]);
                } finally {
                    setIsDriveLoading(false);
                }
            };
            updateContents();
        } else {
            // --- PATH B: The Legacy Fallback ---
            // The parent did not provide a connection, so initialize ourselves
            console.warn(`FileDrive is using legacy self-initialization for matter: ${matterNumber}. This should be refactored.`);
            ActivateDrive();
        }
    // The dependency array handles both new and legacy path reactivity
    }, [preInitializedFolder, preInitializedDrive, matterNumber]);

    const FormSubmitRef = useRef(null);
    const [validated, setValidated] = useState<boolean>(false);
    const [validatedToProcess, setValidatedToProcess] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [nameSort, setNameSort] = useState<SortDir>(SortDir.Up);
    const [modifiedDateSort, setModifiedDateSort] = useState<SortDir>(SortDir.None);

    const getSortIcon = (sortDir: SortDir): JSX.Element => {
        switch(sortDir) {
            case SortDir.None:
                return <i className="fa-solid fa-sort status-secondary-light"></i>
            case SortDir.Up:
                return <i className="fa-duotone fa-sort-up status-secondary"></i>
            case SortDir.Down:
                return <i className="fa-duotone fa-sort-down status-secondary"></i>
        }
    }

    const handleNameSortClick = () => {
        setModifiedDateSort(SortDir.None);
        setCookie("myDriveDocModifiedDateSort", SortDir.None, 365);
        if(nameSort === SortDir.None || nameSort === SortDir.Down) {
            setNameSort(SortDir.Up);
            setCookie("myDriveDocNameSort", SortDir.Up, 365);
            setFolderContent(FileDrive_Funcs.sortFolderContentByNameAsc(FolderContent));
        }
        else {
            setNameSort(SortDir.Down);
            setCookie("myDriveDocNameSort", SortDir.Down, 365);
            setFolderContent(FileDrive_Funcs.sortFolderContentByNameDsc(FolderContent));
        }
    }

    const handleModifiedDateSortClick = () => {
        setNameSort(SortDir.None);
        setCookie("myDriveDocNameSort", SortDir.None, 365);
        if(modifiedDateSort === SortDir.None || modifiedDateSort === SortDir.Down) {
            setModifiedDateSort(SortDir.Up);
            setCookie("myDriveDocModifiedDateSort", SortDir.Up, 365);
            setFolderContent(FileDrive_Funcs.sortFolderContentByModifiedDateAsc(FolderContent));
        }
        else {
            setModifiedDateSort(SortDir.Down);
            setCookie("myDriveDocModifiedDateSort", SortDir.Down, 365);
            setFolderContent(FileDrive_Funcs.sortFolderContentByModifiedDateDsc(FolderContent));
        }
    }

    // Mount-only: restore the sort preferences from cookies.
    useEffect(() => {
        const nameSortCookie = getCookie("myDriveDocNameSort");
        const modifiedDateSortCookie = getCookie("myDriveDocModifiedDateSort");
        if (nameSortCookie) setNameSort(nameSortCookie as SortDir);
        if (modifiedDateSortCookie) setModifiedDateSort(modifiedDateSortCookie as SortDir);
    }, [])

    // Archive read-back keys off the matter + session identity, so it must refetch when any of them change
    // — matterNumber in particular arrives from DataReaderObj?.getFileNumber() and can hydrate AFTER mount;
    // a mount-only fetch would send a blank matter, skip Scan B, and hide new-prefix archived files. The
    // `ignore` flag drops a stale (out-of-order) response so it can't overwrite a newer matter's result.
    useEffect(() => {
        let ignore = false;
        (async () => {
            const data = await FileDrive_Funcs.getArchiveFiles(AppCode, UserID, AppID, SessionID, matterNumber);
            if (!ignore) setArchiveData(data);
        })();
        return () => { ignore = true; };
    }, [AppCode, UserID, AppID, SessionID, matterNumber])

    const checkFileSize = (file): boolean => {
        const MAX_FILE_SIZE = 25 * Math.pow(2, 20); // 25 MB
        if (file.size > MAX_FILE_SIZE) {
            setErrorMsg("This file exceeds the upload limit of 25MB. Please try another file.");
            setValidated(false);
            return false;
        }
        return true;
    }

    const checkFileName = (name: string): boolean => {
        if (hasSpecialCharacter(name, fileSpecialChars)) {
            setValidated(false);
            setErrorMsg(`Document name may not contain special characters eg. ${fileSpecialChars.join("")}.`);
            setValidatedToProcess(false);
            return false;
        }
        return true;
    }

    const checkFolderName = (name: string): boolean => {
        if (hasSpecialCharacter(name, folderSpecialChars)) {
            setValidated(false);
            setErrorMsg(`Folder name may not contain special characters eg. ${folderSpecialChars.join("")}.`);
            setValidatedToProcess(false);
            return false;
        }
        return true;
    }

    const handleGenericReset = () => {
        setValidated(false);
        setErrorMsg(null);
    }

    const [deleteModal, setDeleteModal] = useState<{
        show: boolean,
        folder?: FolderEntry,
        doc?: FileEntry,
    }>({ show: false });
    const ProccessDelete = async (item: "Folder" | "Document"): Promise<void> => {
        try {
            setIsProcessing(true);
            if (item === "Document") {
                await FileDrive_Funcs.deleteDocument(deleteModal.doc);
            } else {
                await FileDrive_Funcs.deleteFolder(deleteModal.folder);
            }
            setIsProcessing(false);
            setDeleteModal({ show: false, folder: null, doc: null });
        } catch (e) {            
            let v = (e && e.message)?e.message:e;
            setErrorMsg(v);
            setIsProcessing(false);
        }
    }

    const [renameModal, setRenameModal] = useState<{
        show: boolean,
        folder?: FolderEntry,
        doc?: FileEntry,
    }>({ show: false });
    const handleRenameSubmit = (event, item: "Folder" | "Document") => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        if (form.checkValidity() && validated) {
            const newName = form.renameNewName.value;
            ProcessRename(newName.trim(), item === "Document" ? "Docuemnt" : "Folder");
        }
    }

    const ProcessRename = async (newName: string, item: "Folder" | "Docuemnt"): Promise<void> => {
        try {
            setIsProcessing(true);
            const Folder = FileDrive_Funcs.returnCurrentFolder();
            let activityMsg = "";
            if (item === "Docuemnt") {
                const currentDocName = renameModal.doc.actualName;
                const newFullName = `${newName}.${renameModal.doc.actualExt}`;
                activityMsg = `Document '${currentDocName}' renamed to '${newName}'`;
                await renameModal.doc.moveTo(Folder, newFullName);
            } else {
                const currentFolderName = renameModal.folder.name;
                await renameModal.folder.moveTo(Folder, newName);
                activityMsg = `Folder '${currentFolderName}' renamed to '${newName}'`;
            }
            setIsProcessing(false);
            setRenameModal({ show: false, folder: null, doc: null });
            activityCenter.Add(activityMsg, null, "success", null);
            FileDrive_Funcs.refreshFolder();
            handleGenericReset();
        } catch (e) {            
            let v = (e && e.message)?e.message:e;
            if (v === "conflict") {
                setErrorMsg(`A ${item === "Docuemnt" ? "file" : "folder "} with this name already exists. Please try a different name.`)
            } else {
                setErrorMsg(v);
            }
            setIsProcessing(false);
        }
    }

    const handleRenameChange = (event, item: "Folder" | "Document") => {
        setValidated(true);
        setErrorMsg(null);
        const form = event.currentTarget;
        if (!form.checkValidity()) {
            return;
        }
        const name = event.currentTarget.renameNewName.value;
        if (item === "Folder") {
            checkFolderName(name);
        } else {
            checkFileName(name);
        }
    }

    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [uploadConflictFullName, setUploadConflictFullName] = useState<string>(null);
    const [uploadNewDocOverwritePop, setUploadNewDocOverwritePop] = useState<boolean>(false);
    const [uploadNewDocPop, setUploadNewDocPop] = useState<boolean>(false);
    const pendingUploadsRef = useRef<UploadQueueItem[]>([]);
    const pendingUploadIndexRef = useRef(0);

    const validateSelectedUploadFiles = (files: File[]): boolean => {
        setErrorMsg(null);
        for (const f of files) {
            if (!checkFileSize(f)) {
                setValidated(false);
                setValidatedToProcess(false);
                return false;
            }
        }
        if (files.length === 1) {
            const { base } = splitFileName(files[0].name);
            if (!checkFileName(base)) {
                setValidatedToProcess(false);
                return false;
            }
            setValidated(true);
            setValidatedToProcess(true);
            return true;
        }
        let namesOk = true;
        for (const f of files) {
            if (!checkFileName(splitFileName(f.name).base)) {
                namesOk = false;
                break;
            }
        }
        setValidated(namesOk);
        setValidatedToProcess(namesOk);
        return true;
    };

    const revalidateMultiUploadForm = (form: HTMLFormElement) => {
        if (uploadFiles.length <= 1) return;
        setValidated(true);
        setErrorMsg(null);
        setValidatedToProcess(true);
        for (let i = 0; i < uploadFiles.length; i++) {
            const f = uploadFiles[i];
            if (!checkFileSize(f)) {
                setValidated(false);
                setValidatedToProcess(false);
                return;
            }
            const nameEl = form.elements.namedItem(`uploadName_${i}`);
            const nameField = (nameEl instanceof HTMLInputElement ? nameEl.value : "").trim();
            if (!nameField) {
                setValidatedToProcess(false);
                return;
            }
            if (!checkFileName(nameField)) {
                setValidatedToProcess(false);
                return;
            }
        }
        setValidated(true);
        setValidatedToProcess(true);
    };

    const buildUploadQueueFromForm = (form: HTMLFormElement): UploadQueueItem[] | null => {
        if (uploadFiles.length === 0) return null;
        if (uploadFiles.length === 1) {
            const f = uploadFiles[0];
            if (!checkFileSize(f)) return null;
            const nameField = (form as any).fileToUploadName?.value?.trim();
            const docExt = (form as any).fileToUploadExt?.value ?? "";
            if (!checkFileName(nameField)) return null;
            const fullName = docExt ? `${nameField}.${docExt}` : nameField;
            return [{ file: f, fullName }];
        }
        setErrorMsg(null);
        const queue: UploadQueueItem[] = [];
        for (let i = 0; i < uploadFiles.length; i++) {
            const f = uploadFiles[i];
            if (!checkFileSize(f)) return null;
            const nameEl = form.elements.namedItem(`uploadName_${i}`);
            const nameField = (nameEl instanceof HTMLInputElement ? nameEl.value : "").trim();
            const extEl = form.elements.namedItem(`uploadExt_${i}`);
            const docExt = (extEl instanceof HTMLInputElement ? extEl.value : "") || "";
            if (!checkFileName(nameField)) return null;
            const fullName = docExt ? `${nameField}.${docExt}` : nameField;
            queue.push({ file: f, fullName });
        }
        return queue;
    };

    const onFileUploadDragEnd = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const files = Array.from(event.dataTransfer.files as Iterable<File>).filter((f) => f.type !== "");
        if (files.length === 0) return;

        setUploadFiles(files);
        setUploadNewDocPop(true);
        validateSelectedUploadFiles(files);

        requestAnimationFrame(() => {
            FormSubmitRef.current?.checkValidity();
        });
    }

    const uploadNewDocPopSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        if (!(form.checkValidity() && validated)) return;
        const queue = buildUploadQueueFromForm(form);
        if (!queue?.length) return;
        pendingUploadsRef.current = queue;
        pendingUploadIndexRef.current = 0;
        void ProcessUploadDocument();
    };

    const ProcessUploadDocument = async (): Promise<void> => {
        const queue = pendingUploadsRef.current;
        const idx = pendingUploadIndexRef.current;
        if (idx >= queue.length) {
            setIsProcessing(false);
            return;
        }
        const item = queue[idx];
        try {
            setIsProcessing(true);
            const Folder = FileDrive_Funcs.returnCurrentFolder();
            try {
                await Folder.getFile(item.fullName);
                setUploadConflictFullName(item.fullName);
                setUploadNewDocOverwritePop(true);
            } catch {
                await ProcessUploadDocumentStep2();
            }
        } catch (e) {
            let v = (e && e.message) ? e.message : e;
            setErrorMsg(v);
            setIsProcessing(false);
        }
    }

    const ProcessUploadDocumentStep2 = async (): Promise<void> => {
        const queue = pendingUploadsRef.current;
        const idx = pendingUploadIndexRef.current;
        if (idx >= queue.length) return;
        const item = queue[idx];
        try {
            const Folder = FileDrive_Funcs.returnCurrentFolder();
            await Folder.getFile(item.fullName, { create: true, overwrite: true }, item.file);
            activityCenter.Add(`Document '${item.fullName}' uploaded.`, null, "success", null);
            pendingUploadIndexRef.current = idx + 1;
            if (pendingUploadIndexRef.current >= queue.length) {
                setIsProcessing(false);
                setUploadNewDocPop(false);
                setUploadNewDocOverwritePop(false);
                handleUploadReset();
                FileDrive_Funcs.refreshFolder();
            } else {
                await ProcessUploadDocument();
            }
        } catch (e) {
            let v = (e && e.message) ? e.message : e;
            setErrorMsg(v);
            setIsProcessing(false);
        }
    }

    const uploadNewDocPopChange = (e: React.FormEvent<HTMLFormElement>) => {
        if (uploadFiles.length === 0) return;
        const form = e.currentTarget;

        if (uploadFiles.length === 1) {
            const f = uploadFiles[0];
            setValidated(true);
            setErrorMsg(null);
            setValidatedToProcess(true);
            if (!checkFileSize(f)) {
                setValidated(false);
                setValidatedToProcess(false);
                return;
            }
            if (!form.checkValidity()) {
                setValidatedToProcess(false);
                return;
            }
            const filename = (form as any).fileToUploadName?.value;
            checkFileName(filename);
            return;
        }

        revalidateMultiUploadForm(form);
    };

    const handleUploadReset = () => {
        setValidated(false);
        setErrorMsg(null);
        setUploadFiles([]);
        setUploadConflictFullName(null);
        pendingUploadsRef.current = [];
        pendingUploadIndexRef.current = 0;
    };

    const [createNewFolderPop, setCreateNewFolderPop] = useState<boolean>(false);
    const handleNewFolderSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        if (form.checkValidity() && validated) {
            const folderName = form.createNewSubFolderName.value;
            ProcessNewSubfolder(folderName.trim());
        }
    }

    const ProcessNewSubfolder = async (folderName: string): Promise<void> => {
        try {
            setIsProcessing(true);
            let Folder = FileDrive_Funcs.returnCurrentFolder();
            await Folder.getFolder(folderName, { create: true })
            setIsProcessing(false);
            setCreateNewFolderPop(false);
            activityCenter.Add(`Folder '${folderName}' created.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
            handleGenericReset();
        } catch (e) {            
            let v = (e && e.message)?e.message:e;
            setErrorMsg(v);
            setIsProcessing(false);
        }
    }

    const handleNewFolderChange = (e) => {
        setValidated(true);
        setErrorMsg(null);
        const form = e.currentTarget;
        if (!form.checkValidity()) {
            return;
        }
        const name = e.currentTarget.createNewSubFolderName.value;
        checkFolderName(name);
    }

    const [copyFilePop, setCopyFilePop] = useState<{ show: boolean, doc: FileEntry }>({ show: false, doc: null });
    const handleCopyDocumentSubmit = (event) => {
        event.preventDefault();
        event.stopPropagation();
    
        const form = event.currentTarget;
        const newFileName = form.copyFileNewName.value.trim();
        
        setErrorMsg(null);
        setValidated(false);
    
        // Basic form validation
        if (form.checkValidity() === false) {
            setValidated(true);
            return;
        }
    
        // Check for special characters
        if (!checkFileName(newFileName)) {
            setErrorMsg("File name contains invalid characters.");
            setValidated(true);
            return;
        }
    
        // Check for existing file names
        const newFullName = `${newFileName}.${copyFilePop.doc.ext}`;
        const existingFileNames = FolderContent
            .filter((item): item is FileEntry => item.isFile)
            .map(file => file.name.toLowerCase());
    
        if (existingFileNames.includes(newFullName.toLowerCase())) {
            setErrorMsg("A file with this name already exists. Please try a different name.");
            setValidated(false);
            return;
        }
    
        // If all validations pass, proceed with copying
        setValidated(true);
        ProcessCopyDocument(newFileName);
    }

    const ProcessCopyDocument = async (newFileName: string): Promise<void> => {
        try {
            setIsProcessing(true);
            const newFullName = `${newFileName}.${copyFilePop.doc.actualExt}`;
            await copyFilePop.doc.copyTo(null, newFullName);
            setIsProcessing(false);
            setCopyFilePop({ show: false, doc: null });
            setErrorMsg(null);
            activityCenter.Add(`New file '${newFileName}' created successfully.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
        } catch (e) {
            let v = (e && e.message)?e.message:e;
            if (v === "conflict") {
                setErrorMsg(`A file with this name already exists. Please try a different name.`)
            } else {
                setErrorMsg(v);
            }
            setIsProcessing(false);
        } finally {
            setValidated(false);
        }
    }

    const [moveFilePop, setMoveFilePop] = useState<{ show: boolean, doc: FileEntry }>({ show: false, doc: null });
    const handleMoveDocumentSubmit = (event) => {
        const form = event.currentTarget;
        var folderName = form.moveFileNewFolder.value;
        setErrorMsg(null)
        event.preventDefault();
        event.stopPropagation();
        ProcessMoveDocument(folderName);
    }

    const ProcessMoveDocument = async (folderName): Promise<void> => {
        try {
            setIsProcessing(true);
            let FileToMove = moveFilePop.doc;

            let CurrentFolder = FileDrive_Funcs.returnCurrentFolder();
            let newFolder: FolderEntry;
            if (folderName === "ldd_parent") {
                newFolder = await CurrentFolder.getParent();
            } else {
                newFolder = await CurrentFolder.getFolder(folderName);
            }
            await FileToMove.moveTo(newFolder);
            setIsProcessing(false);
            setMoveFilePop({ show: false, doc: null });
            setErrorMsg(null);
            activityCenter.Add(`File '${FileToMove.name}' moved to '${newFolder.name}'.`, null, "success", null);
            FileDrive_Funcs.refreshFolder();
        } catch (e) {            
            let v = (e && e.message)?e.message:e;
            if (v === "conflict") {
                setErrorMsg(`A file with this name already exists. Please try a different name.`)
            } else {
                setErrorMsg(v);
            }
            setIsProcessing(false);
        }
    }

    const [createListArchivePop, setCreateListArchivePop] = useState<boolean>(false);
    const [treeFinished, setTreeFinished] = useState<TreeNode>(null);
    const [archiveData, setArchiveData] = useState<Array<ArchiveData>>([]);
    const getArchivedFiles = async (): Promise<void> => {
        try{
            setCreateListArchivePop(true);
            archiveData.map(archiveData => addToTree(archiveData, root));
            setTreeFinished(root);
        } catch (error) {
            console.error("Failed to get archive files:", error);
        }
    }

    const handleLocalDownload = async (downloadUrl: string, fileName: string) => {
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = fileName;
        a.click();
        a.remove();
    };

    const root: TreeNode = {name: "root", children:[]};
    const addToTree = (archiveData: ArchiveData, root: TreeNode): void => {
        const parts = archiveData.filePath.split("/").filter(Boolean);
        let current = root;
        for (let i = 0; i < parts.length; i++){
            const part = parts[i];
            const isFile = i === parts.length - 1;

            let included = current.children.find(node => node.name === part);

            if (!included){
                included = {
                    name: part,
                    ...(isFile? {fileUrl: archiveData.fileDownloadUrl}: {children:[]}) 
                };
                current.children.push(included);
            }

            current = included;
        }
    }

    const renderNode = (node: TreeNode, indent: number) => (
        <React.Fragment key={node.name}>
            <tr onClick={() => {
                    if (node.fileUrl) {
                        handleLocalDownload(node.fileUrl, node.name);
                    }
                }}
                
                className={node.fileUrl ? "":"non-clickable-tr"}>
                <td>
                    <i className={node.fileUrl ? "fa-regular fa-file" : "fa-solid fa-folder"} style={{ paddingLeft: `${indent * 25}px`, paddingRight: '8px' }}></i>
                    <span>{node.name}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                    {node.fileUrl && (
                    <i
                        className="fa-solid fa-arrow-down-to-line"
                    />
                    )}
                </td>
            </tr>
            {node.children?.map(child => renderNode(child, indent + 1))}
        </React.Fragment>
    );


    const renderTree = (root: TreeNode) => {
        return root.children?.length === 0 ? (
            <ListPlaceholder icon="fa-light fa-box-archive" title="No Archived Documents" msg="Documents are archived 18 months after file closing"></ListPlaceholder>
        ) : (
            <Table hover>
                <tbody>
                    {root.children.map(node => renderNode(node, 0))}
                </tbody>
            </Table>
        )
    } 

    useEffect(() => {
        FileDrive_Funcs.refreshFolder();
    }, [FileDriveIndicators])

    // Show loading while drive is initializing (works for both paths)
    if (isDriveLoading) {
        return <Loading extraMsg="Opening MyDrive" showMsg={true} />;
    }
    
    return (
        <>
            {createListArchivePop &&
                <Modal centered show={true} size="lg" className="modal-list">
                    <Modal.Header>
                        <h2>Documents Archive</h2>
                    </Modal.Header>
                    <Modal.Body>
                        {!treeFinished ?
                            <LoadingIcon showMsg={true} extraMsg="Loading Documents" inline={true} />
                            :
                            <>
                                {renderTree(treeFinished)}
                            </>
                        }
                        
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-primary" className="archiveCancelButton" onClick={() => setCreateListArchivePop(false)}>Close</Button>
                    </Modal.Footer>
                </Modal>
            }
            {createNewFolderPop &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>New Folder</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Creating Folder" inline={true} />
                            :
                            <Form noValidate autoComplete="off" validated={validated}
                                onSubmit={handleNewFolderSubmit}
                                onChange={handleNewFolderChange}>

                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Label>Folder Name</Form.Label>
                                <Form.Group controlId="createNewSubFolderName">
                                    <Form.Control required type="test" placeholder="enter folder name.." autoFocus />
                                    <Form.Control.Feedback type="invalid">Field cannot be empty.</Form.Control.Feedback>
                                </Form.Group>

                                <Button ref={FormSubmitRef} type="submit" style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={() => { handleGenericReset(); setCreateNewFolderPop(false); }}>Cancel</Button>
                                <Button variant="primary" onClick={() => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Create</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            {uploadNewDocPop &&
                <Modal centered show={true} size={uploadFiles.length > 1 ? "lg" : "sm"}>
                    <Modal.Header>
                        <Modal.Title>Upload File to {FileDrive_Funcs.returnCurrentFolder()?.name}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Uploading document(s)" inline={true} />
                            :
                            <Form noValidate validated={validated}
                                key={
                                    uploadFiles.length > 1
                                        ? `multi-all-${uploadFiles.map((f, i) => `${f.name}-${i}-${f.size}`).join("|")}`
                                        : uploadFiles.length === 1
                                            ? `single-${uploadFiles[0].name}-${uploadFiles[0].size}`
                                            : "pick-files"
                                }
                                autoComplete="off"
                                onSubmit={uploadNewDocPopSubmit}
                                onChange={uploadFiles.length === 1 ? uploadNewDocPopChange : undefined}>
                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}
                                {uploadFiles.length === 1 ? (
                                    <>
                                        <Form.Label>File Name</Form.Label>
                                        <Form.Group as={InputGroup}>
                                            <Form.Control required type="text" id="fileToUploadName"
                                                placeholder="please enter file name.."
                                                autoFocus
                                                defaultValue={splitFileName(uploadFiles[0].name).base} />
                                            <Form.Control bsPrefix="input-group-text" id="fileToUploadExt"
                                                style={{ maxWidth: "64px" }}
                                                defaultValue={splitFileName(uploadFiles[0].name).ext}
                                                disabled
                                            />
                                            <Form.Control.Feedback type="invalid">Please fill in the new document name</Form.Control.Feedback>
                                        </Form.Group>
                                    </>
                                ) : uploadFiles.length > 1 ? (
                                    <>
                                        <p className="text-muted small mb-2">Rename each file before uploading.</p>
                                        <div className="border rounded" style={{ maxHeight: "min(360px, 55vh)", overflowY: "auto" }}>
                                            <Table responsive size="sm" className="mb-0 align-middle">
                                                <thead className="table-light position-sticky top-0">
                                                    <tr>
                                                        <th style={{ minWidth: "140px" }}>Original file</th>
                                                        <th>Name</th>
                                                        <th style={{ width: "72px" }} className="text-nowrap">Ext</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {uploadFiles.map((f, i) => {
                                                        const { base, ext } = splitFileName(f.name);
                                                        return (
                                                            <tr key={`${f.name}-${i}-${f.size}`}>
                                                                <td className="small text-break text-muted" title={f.name}>{f.name}</td>
                                                                <td>
                                                                    <Form.Control
                                                                        required
                                                                        type="text"
                                                                        name={`uploadName_${i}`}
                                                                        placeholder="File name"
                                                                        defaultValue={base}
                                                                        autoFocus={i === 0}
                                                                        className="form-control-sm"
                                                                        onChange={(ev) => {
                                                                            const form = (ev.currentTarget as HTMLInputElement).closest("form");
                                                                            if (form) revalidateMultiUploadForm(form as HTMLFormElement);
                                                                        }}
                                                                    />
                                                                    <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                                                                </td>
                                                                <td className="small font-monospace text-muted">
                                                                    <input type="hidden" name={`uploadExt_${i}`} value={ext} />
                                                                    {ext || "—"}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                ) : (
                                    <Form.Group controlId="fileUploader">
                                        <Form.Control
                                            onChange={(e) => {
                                                const el = e.target as HTMLInputElement;
                                                const fl = Array.from(el.files || []).filter((f) => f.type !== "");
                                                if (fl.length === 0) {
                                                    el.value = "";
                                                    return;
                                                }
                                                setUploadFiles(fl);
                                                validateSelectedUploadFiles(fl);
                                            }}
                                            className="form-upload"
                                            type="file"
                                            multiple
                                        />
                                        <Form.Control.Feedback type="invalid">Please select file(s) to upload</Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={() => { handleUploadReset(); setUploadNewDocPop(false); }}>Cancel</Button>
                                {(uploadFiles.length > 0 && validatedToProcess) ?
                                    <Button variant="primary" onClick={() => {
                                        if (FormSubmitRef.current) {
                                            FormSubmitRef.current.click();
                                        }
                                    }}>Upload</Button>
                                    :
                                    <Button variant="secondary">Upload</Button>}
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            {uploadNewDocOverwritePop &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Upload New File</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="warning">You will lose your previous document content.</Alert>
                        <span>Document <b>{uploadConflictFullName}</b> already exists, would you like to replace it?</span>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="text" onClick={() => { setUploadNewDocOverwritePop(false); setIsProcessing(false); handleUploadReset(); }}>Cancel</Button>
                        <Button variant="primary" onClick={() => { setUploadNewDocOverwritePop(false); void ProcessUploadDocumentStep2(); }}>Replace</Button>
                    </Modal.Footer>
                </Modal>
            }

            {moveFilePop.show &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title className="text-truncate">Move "{moveFilePop.doc.name}"</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg="Moving Document" inline={true} />
                            :
                            <Form noValidate onSubmit={handleMoveDocumentSubmit}>
                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Group>
                                    <Form.Label>Current Location</Form.Label>
                                    <Form.Control disabled required type="text" defaultValue={FileDrive_Funcs.returnCurrentFolder().name} />
                                </Form.Group>

                                <br />

                                <Form.Group controlId="moveFileNewFolder">
                                    <Form.Label>New Location</Form.Label>
                                    <Form.Select style={{ padding: '5px' }}>
                                        {!FileDrive_Funcs.isMainFolder() && <option value="ldd_parent">../ (parent folder)</option>}
                                        {FolderContent.filter((v: FileEntry | FolderEntry) => !v.isFile).map((folder: FolderEntry, index: number) => {
                                            return <option value={folder.name} key={`movedocOption${index}`}>{folder.name}</option>
                                        })}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">Please specify the folder to move this document to.</Form.Control.Feedback>
                                </Form.Group>

                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={() => { handleGenericReset(); setMoveFilePop({ show: false, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={() => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Move File</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>}

                {copyFilePop.show &&
        <Modal centered show={true} size="sm">
            <Modal.Header>
                <Modal.Title className="text-truncate">Copy "{copyFilePop.doc.name}"</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isProcessing ?
                    <LoadingIcon showMsg={true} extraMsg="Copying Document" inline={true} />
                    :
                    <Form noValidate validated={validated} autoComplete="off"
                        onSubmit={handleCopyDocumentSubmit}
                        onChange={(e) => {
                            const filename = e.currentTarget.copyFileNewName.value;
                            const isEmpty = filename.trim() === '';
                            const isUnchanged = filename === copyFilePop.doc.justName;
                            const hasNoSpecialChars = !hasSpecialCharacter(filename, fileSpecialChars);
                            setValidated(hasNoSpecialChars && !isUnchanged && !isEmpty);
                        }}>

                        {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                        <Form.Label>File Name</Form.Label>
                        <Form.Group controlId="copyFileNewName">
                            <Form.Control 
                                required 
                                type="text" 
                                placeholder="Please enter new document name" 
                                autoFocus 
                                defaultValue={copyFilePop.doc.justName} 
                            />
                            <Form.Control.Feedback type="invalid">Please enter a new, non-empty, valid document name</Form.Control.Feedback>
                        </Form.Group>
                        <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                    </Form>
                }
            </Modal.Body>
            <Modal.Footer>
            {!isProcessing &&
                <>
                                    <Button variant="text" onClick={() => { handleGenericReset(); setCopyFilePop({ show: false, doc: null }) }}>Cancel</Button>
                                    <Button 
                                        variant="primary" 
                                        onClick={() => {
                            if (FormSubmitRef.current) {
                                FormSubmitRef.current.click()
                            }
                        }}
                        disabled={!validated}
                    >
                        Copy
                    </Button>
                </>
            }
        </Modal.Footer>
    </Modal>
            }

            {renameModal.show &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title className="text-truncate">Rename "{(renameModal.doc === undefined) ? renameModal.folder.name : renameModal.doc.justName}"</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg={`Renaming ${(renameModal.doc === undefined) ? "Folder" : "Document"}`} inline={true} />
                            :
                            <Form noValidate autoComplete="off" validated={validated}
                                onSubmit={(e) => { handleRenameSubmit(e, (renameModal.doc === undefined) ? "Folder" : "Document") }}
                                onChange={(e) => { handleRenameChange(e, (renameModal.doc === undefined) ? "Folder" : "Document") }}>

                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}

                                <Form.Label>{renameModal.doc === undefined ? "Folder" : "File"} Name</Form.Label>
                                <Form.Group as={InputGroup}>
                                    <Form.Control required type="test" id="renameNewName"
                                        placeholder={(renameModal.doc === undefined) ? renameModal.folder.name : renameModal.doc.justName}
                                        autoFocus />
                                    {renameModal.doc && <Form.Control disabled id="fileToUploadExt" style={{ maxWidth: "64px" }} defaultValue={renameModal.doc.ext} />}
                                    <Form.Control.Feedback type="invalid">Please enter a new {(renameModal.doc === undefined) ? "folder" : "document"} name</Form.Control.Feedback>
                                </Form.Group>

                                <Button type="submit" ref={FormSubmitRef} style={{ display: "none" }}></Button>
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={() => { handleGenericReset(); setRenameModal({ show: false, folder: null, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={() => {
                                    if (FormSubmitRef.current) {
                                        FormSubmitRef.current.click()
                                    }
                                }}>Rename</Button>
                            </>}
                    </Modal.Footer>
                </Modal >
            }

            {deleteModal.show &&
                <Modal centered show={true} size="sm">
                    <Modal.Header>
                        <Modal.Title>Delete permanently?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {isProcessing ?
                            <LoadingIcon showMsg={true} extraMsg={`Deleting ${(deleteModal.doc === undefined) ? "Folder" : "Document"}`} inline={true} />
                            :
                            <>
                                {errorMsg > "" && <Alert variant="danger">{errorMsg}</Alert>}
                                {deleteModal.doc === undefined ?
                                    <>
                                        <Alert variant="warning">Folders with contents can not be deleted. If this folder has contents, please delete them first.</Alert>
                                        <span>"{deleteModal.folder.name}" will be <b>permanently deleted</b> and you will not be able to restore it. Are you sure you want to delete it?</span>
                                    </> :
                                    <>
                                        <span>"{deleteModal.doc.name}" will be <b>permanently deleted</b> and you will not be able to restore it. Are you sure you want to delete it?</span>
                                    </>
                                }
                            </>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        {!isProcessing &&
                            <>
                                <Button variant="text" onClick={() => { handleGenericReset(); setDeleteModal({ show: false, folder: null, doc: null }) }}>Cancel</Button>
                                <Button variant="primary" onClick={() => { ProccessDelete((deleteModal.doc === undefined) ? "Folder" : "Document"); }}>Delete</Button>
                            </>
                        }
                    </Modal.Footer>
                </Modal>
            }

            <div ref={ref}
                onDrop={onFileUploadDragEnd}
                onDragOver={(e) => { e.preventDefault(); return false; }}
                onDragEnter={(e) => { e.preventDefault(); return false; }}
                className="d-flex flex-column flex-fill h-100"
                style={{ flex: 1 }}>
                <Card className="h-100 d-flex flex-column myDriveContainer m-0">
                    <Card.Header>
                        <div>
                            <Button variant="link-text" title="Parent folder"><i onClick={() => FileDrive_Funcs.getParentFolder()} className="fa-solid fa-arrow-up" title=""></i></Button>
                            <Button variant="link-text" title="Refresh" onClick={FileDrive_Funcs.refreshFolder}><i className="fa-solid fa-rotate"></i></Button>
                            <Button variant="link-text" title="Home" onClick={() => FileDrive_Funcs.getMainFolder()}><i className="fa-solid fa-house"></i></Button>
                            <b style={{marginLeft:'5px'}}>/ {FileDrive_Funcs.drawPath()}</b>
                        </div>

                        {!onPickFile && <>
                            <Button variant="link" title="Create new subfolder" onClick={() => { setCreateNewFolderPop(true); }}><i className="fa-solid fa-folder-plus"></i> Folder</Button>
                            <Button variant="link" title="Upload new document" onClick={() => { setUploadNewDocPop(true); }}><i className="fa-solid fa-arrow-up-to-line"></i> Upload</Button>
                        </>}
                        <CloseButton onClick={() => closeMyDrive()} />
                    </Card.Header>

                    {!onPickFile && archiveData.length !== 0 && (
                        <Alert variant="primary" style={{ margin: '10px', padding:'10px 15px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginLeft: '15px' }}>
                                <span>
                                    Documents have been archived. You can access these documents through the <b>View Archive</b> button.
                                </span>
                                <Button variant="primary" onClick={() => { getArchivedFiles() }}>
                                    View Archive
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Card.Body className="overflow-auto p-0">{(isDriveLoading) ?
                        <Loading extraMsg="Loading Folder" showMsg={true} />
                        :
                        (FolderContent.length === 0 ?
                            <ListPlaceholder icon="fa-light fa-file-circle-plus" title='Drop a document here' msg="or generate one from Documents" extra="" />
                            :
                            <>
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '30px' }}></th>
                                            <th>
                                                <button 
                                                    onClick={() => handleNameSortClick()}
                                                    style={{ border: 'none', fontSize: '13px', fontWeight: 700, backgroundColor: 'transparent', padding: '0px'}}
                                                >
                                                    {getSortIcon(nameSort)}Name
                                                </button>
                                            </th>
                                            <th style={{ width: '40px' }}></th>
                                            <th style={{ width: '190px' }}>
                                                <button 
                                                    onClick={() => handleModifiedDateSortClick()}
                                                    style={{ border: 'none', fontSize: '13px', fontWeight: 700, backgroundColor: 'transparent', padding: '0px'}}
                                                >
                                                    {getSortIcon(modifiedDateSort)}Modified
                                                </button>
                                            </th>
                                            <th style={{ width: '130px' }}></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                            {FolderContent.filter((v: FileEntry | FolderEntry) => !v.isFile).map((folder: FolderEntry, index: number) => {
                                                return <FolderRow
                                                    key={`driveFolder${index}`}
                                                    folder={folder}
                                                    renameF={(setRenameModal)}
                                                    deleteF={(setDeleteModal)}
                                                    pickerMode={!!onPickFile} />
                                            })}
                                            {FolderContent.filter((v: FileEntry | FolderEntry) => v.isFile).map((doc: FileEntry, index: number) => {
                                                return <DocumentRow
                                                    key={`driveDocument${index}`}
                                                    doc={doc}
                                                    renameD={setRenameModal}
                                                    deleteD={setDeleteModal}
                                                    moveD={setMoveFilePop}
                                                    copyD={setCopyFilePop}
                                                    portalState={portalState}
                                                    AppID={AppID}
                                                    UserID={UserID}
                                                    AppCode={AppCode}
                                                    SessionID={SessionID}
                                                    portalService={portalService}
                                                    onPickFile={onPickFile}
                                                />
                                            })}
                                        </tbody>
                                    </Table>
                                </>
                            )
                        }
                    </Card.Body>
                </Card>
            </div>
        </>
    )
})


